import { RestClient } from "@shared/types/common";
import { MY_JFROG_APIS } from "./myjfrogApis.ts";
import {
  SESSION_GET_LINKED_SESSION,
  USER_LOGIN,
  USER_LOGOUT,
  USER_URL,
  USER_VALIDATE_EMAIL,
} from "@shared/services/tour/urls/dashboard-urls.ts";
import logger from "@shared/utils/logger.ts";
import { constants } from "@shared/constants/constants.ts";
import { TourUrlHandler } from "@shared/types/localTypes/tour.ts";
import { useSubscriptionsStore } from "@shared/stores/subscriptions.ts";

type RestTour = RestClient;

const allServerNames = () =>
  useSubscriptionsStore().currentSubscription?.jpus.map(
    (jpu) => jpu.serverName,
  ) || [];

const getMyJFrogApi = (url: string): TourUrlHandler => {
  let modifiedUrl = url
    .replace(constants.UUID_REGEX, ":uuid")
    .replace(constants.CONTAINS_EMAIL_REGEX, ":email")
    // Replace path segments that are only digits with '*'
    .replace(/\/(\d+)(?=\/|\?|$)/g, "/*")
    // Replace email query param value with '*'
    .replace(/([?&]email=)[^&]+/g, "$1*")
    // Replace billing account specific patterns
    .replace(/billing-accounts\/JFROGACC-\d+/g, "billing-accounts/JFROGACC-*")
    .replace(/subscriptions\/JFROGACC--S\d+/g, "subscriptions/JFROGACC--S*")
    .replace(/limitMonth=\d+/g, "limitMonth=*")
    .replace(/([?&](?:startDate|endDate)=)\d+/g, "$1*")
    // Normalize domain segment in domain-validation URLs
    .replace(/(subscriptions\/\*\/domains)\/[^/?]+/g, "$1/:domain")
    // Normalize verification method segment in domain-validation URLs
    .replace(
      /(subscriptions\/\*\/domains\/:domain)\/(dns_txt_record|manual)\//g,
      "$1/:method/",
    )
    // Strip optional ?domain=… query param on the domains list endpoint
    .replace(/(\/domains)\?domain=[^&]+/g, "$1")
    .replace(/limitMonth=\d+/g, "limitMonth=*")
    // Strip pagination/search query string from owners endpoint
    .replace(/(\/owners)\?[^/]*$/, "$1");

  allServerNames().forEach((serverName) => {
    const serverNameRegex = new RegExp(`/${serverName}/`, "g");
    modifiedUrl = modifiedUrl.replace(serverNameRegex, "/:serverName/");
  });

  return MY_JFROG_APIS[modifiedUrl as keyof typeof MY_JFROG_APIS];
};

const updateUrl = (url: string) => {
  const excludedUrls = [
    USER_LOGIN,
    USER_URL,
    USER_LOGOUT,
    USER_VALIDATE_EMAIL,
    SESSION_GET_LINKED_SESSION,
  ];
  if (excludedUrls.includes(url)) {
    return;
  }

  logger.error("URL needs to be prepared for tour: ", url);
};

export const restTourCreator = (axiosRestClient: RestClient) => {
  return {
    get: async (url: string, config?: object) => {
      const myJFrogApi = getMyJFrogApi(url);
      if (!myJFrogApi) {
        updateUrl(url);
        return await axiosRestClient.get(url, config);
      }

      return myJFrogApi({}, { originalUrl: url, method: "GET" });
    },
    post: async (url: string, data: object, config?: object) => {
      const myJFrogApi = getMyJFrogApi(url);
      if (!myJFrogApi) {
        updateUrl(url);
        return await axiosRestClient.post(url, data, config);
      }
      return myJFrogApi(data, { originalUrl: url, method: "POST" });
    },
    put: async (url: string, data: object, config?: object) => {
      const myJFrogApi = getMyJFrogApi(url);
      if (!myJFrogApi) {
        updateUrl(url);
        return await axiosRestClient.put(url, data, config);
      }
      return myJFrogApi(data, { originalUrl: url, method: "PUT" });
    },
    patch: async (url: string, data: object) => {
      const myJFrogApi = getMyJFrogApi(url);
      if (!myJFrogApi) {
        updateUrl(url);
        return await axiosRestClient.delete(url, data);
      }
      return myJFrogApi(data, { originalUrl: url, method: "PATCH" });
    },
    delete: async (url: string, data: object) => {
      const myJFrogApi = getMyJFrogApi(url);
      if (!myJFrogApi) {
        updateUrl(url);
        return await axiosRestClient.delete(url, data);
      }
      return myJFrogApi(data, { originalUrl: url, method: "DELETE" });
    },
  } as RestTour;
};
